import { useMemo } from "react";
import { EarningsCalendar } from "@features/api/openData";
import type {
  EarningsTableData,
  HistoricalEarningsData,
} from "@components/StockDetails/StockEarning/types";
import {
  calculateSurprise,
  determineResult,
  extractEPSFromFormatted,
  extractFormattedValue,
  filterLatestNonEmptyQuarter,
  filterAllNonEmptyQuarters,
  formatFiscalPeriod,
  formatEPSValue,
  formatRevenueDisplay,
  formatSurprise,
} from "@utils/earnings";

/**
 * 转换为 EarningsTableData
 * 先规范化数值，确保显示值与计算值一致
 */
function transformToEarningsTableData(
  item: EarningsCalendar,
): EarningsTableData {
  // 步骤1: 格式化显示值
  const formattedEpsActual = formatEPSValue(item.eps_actual);
  const formattedEpsEstimate = formatEPSValue(item.eps_estimate);
  const formattedRevenueActual = formatRevenueDisplay(item.revenue_actual);
  const formattedRevenueEstimate = formatRevenueDisplay(item.revenue_estimate);

  // 步骤2: 基于格式化后的值计算 Surprise（确保与用户屏幕显示的值一致）
  const epsSurprise = calculateSurprise(
    extractEPSFromFormatted(formattedEpsActual),
    extractEPSFromFormatted(formattedEpsEstimate),
  );
  const revenueSurprise = calculateSurprise(
    extractFormattedValue(parseFloat(String(item.revenue_actual))),
    extractFormattedValue(parseFloat(String(item.revenue_estimate))),
  );

  return {
    fiscal_period: formatFiscalPeriod(item.quarter, item.year),
    quarter: item.quarter || "",
    year: toNumber(item.year),
    date: item.date,

    eps_estimate: formattedEpsEstimate,
    eps_actual: formattedEpsActual,
    eps_surprise: formatSurprise(epsSurprise),

    revenue_estimate: formattedRevenueEstimate,
    revenue_actual: formattedRevenueActual,
    revenue_surprise: formatSurprise(revenueSurprise),
  };
}

/**
 * 安全地转换为数字
 * API 返回的数值字段都是字符串，需要转换
 */
function toNumber(value?: string | number | null): number {
  if (value == null) {
    return 0;
  }
  const num = typeof value === "string" ? parseFloat(value) : value;
  return isNaN(num) ? 0 : num;
}

/**
 * 转换为 HistoricalEarningsData
 * 先规范化数值，确保显示值与计算值一致
 */
function transformToHistoricalEarningsData(
  item: EarningsCalendar,
): HistoricalEarningsData {
  // 步骤1: 格式化显示值
  const formattedEpsActual = formatEPSValue(item.eps_actual);
  const formattedEpsEstimate = formatEPSValue(item.eps_estimate);
  const formattedRevenueActual = formatRevenueDisplay(item.revenue_actual);
  const formattedRevenueEstimate = formatRevenueDisplay(item.revenue_estimate);

  // 步骤2: 基于格式化后的值计算 Surprise（确保与用户屏幕显示的值一致）
  const epsSurprise = calculateSurprise(
    extractEPSFromFormatted(formattedEpsActual),
    extractEPSFromFormatted(formattedEpsEstimate),
  );
  const revenueSurprise = calculateSurprise(
    extractFormattedValue(item.revenue_actual),
    extractFormattedValue(item.revenue_estimate),
  );

  return {
    fiscal_period: formatFiscalPeriod(item.quarter, item.year),
    quarter: item.quarter,
    year: toNumber(item.year),

    eps_estimate: formattedEpsEstimate,
    eps_actual: formattedEpsActual,
    eps_surprise: formatSurprise(epsSurprise),
    eps_result: determineResult(epsSurprise),

    revenue_estimate: formattedRevenueEstimate,
    revenue_actual: formattedRevenueActual,
    revenue_surprise: formatSurprise(revenueSurprise),
    revenue_result: determineResult(revenueSurprise),
  };
}

/**
 * 财报数据处理 Hook
 */
export function useEarningsData(earnings?: EarningsCalendar[]) {
  // 最新非空季度（用于 EarningsTable）
  const latestQuarter = useMemo(() => {
    const latest = filterLatestNonEmptyQuarter(earnings);
    if (!latest) {
      return null;
    }
    return transformToEarningsTableData(latest);
  }, [earnings]);

  // 所有非空季度（用于 HistoricalEarnings）
  const validQuarters = useMemo(() => {
    const allValid = filterAllNonEmptyQuarters(earnings);
    return allValid.map(transformToHistoricalEarningsData);
  }, [earnings]);

  return {
    latestQuarter,
    validQuarters,
  };
}
